import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Music, BookOpen, Users, Calendar, Award, Heart, Globe, Drum,
  ArrowLeft, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icons
const IconMap = { Music, BookOpen, Users, Calendar, Award, Heart, Globe, Drum };

// Block renderer component
const BlockRenderer = ({ block }) => {
  const { type, props } = block;

  switch (type) {
    case 'hero':
      return (
        <motion.section 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative min-h-[500px] bg-cover bg-center flex items-center justify-center text-center"
          style={{ backgroundImage: `url(${props.backgroundImage})` }}
        >
          <div className={`absolute inset-0 ${props.overlay === 'dark' ? 'bg-black/60' : 'bg-white/60'}`} />
          <div className="relative z-10 px-6 max-w-4xl mx-auto">
            <motion.h1 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`text-5xl md:text-6xl font-bold mb-4 ${props.overlay === 'dark' ? 'text-white' : 'text-gray-900'}`}
            >
              {props.title}
            </motion.h1>
            <motion.p 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className={`text-xl md:text-2xl mb-8 ${props.overlay === 'dark' ? 'text-white/80' : 'text-gray-600'}`}
            >
              {props.subtitle}
            </motion.p>
            {props.buttonText && (
              <motion.a
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                href={props.buttonLink || '#'}
                className="inline-block px-8 py-4 bg-green-500 hover:bg-green-400 text-white font-bold text-lg rounded-full transition-colors"
              >
                {props.buttonText}
              </motion.a>
            )}
          </div>
        </motion.section>
      );

    case 'text':
      return (
        <section className={`py-12 px-6 text-${props.align || 'left'}`}>
          <div className="max-w-4xl mx-auto">
            <p className={`text-gray-700 leading-relaxed ${
              props.size === 'large' ? 'text-xl' : props.size === 'small' ? 'text-sm' : 'text-base'
            }`}>
              {props.content}
            </p>
          </div>
        </section>
      );

    case 'heading': {
      const HeadingTag = props.level || 'h2';
      const sizes = { h1: 'text-5xl', h2: 'text-4xl', h3: 'text-3xl', h4: 'text-2xl' };
      return (
        <section className={`py-8 px-6 text-${props.align || 'center'}`}>
          <div className="max-w-4xl mx-auto">
            <HeadingTag className={`font-bold text-gray-900 ${sizes[props.level] || sizes.h2}`}>
              {props.text}
            </HeadingTag>
          </div>
        </section>
      );
    }

    case 'image':
      return (
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <img loading="lazy" decoding="async" 
              src={props.src} 
              alt={props.alt || ''}
              className={`rounded-xl shadow-lg ${
                props.size === 'full' ? 'w-full' : 
                props.size === 'medium' ? 'max-w-2xl mx-auto' : 
                'max-w-md mx-auto'
              }`}
            />
            {props.caption && (
              <p className="text-center text-gray-500 mt-4 text-sm">{props.caption}</p>
            )}
          </div>
        </section>
      );

    case 'button': {
      const styles = {
        primary: 'bg-green-500 hover:bg-green-400 text-white',
        secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
        outline: 'border-2 border-green-500 text-green-600 hover:bg-green-50',
      };
      const sizeClasses = {
        small: 'px-4 py-2 text-sm',
        medium: 'px-6 py-3',
        large: 'px-8 py-4 text-lg',
      };
      return (
        <section className={`py-6 px-6 text-${props.align || 'center'}`}>
          <div className="max-w-4xl mx-auto">
            <a 
              href={props.link || '#'}
              className={`inline-block rounded-full font-bold transition-colors ${styles[props.style] || styles.primary} ${sizeClasses[props.size] || sizeClasses.medium}`}
            >
              {props.text}
            </a>
          </div>
        </section>
      );
    }

    case 'columns': {
      const gapClasses = { small: 'gap-4', medium: 'gap-8', large: 'gap-12' };
      return (
        <section className="py-12 px-6">
          <div className={`max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-${props.columns || 2} ${gapClasses[props.gap] || gapClasses.medium}`}>
            {props.content?.map((col, i) => (
              <div key={i} className="bg-gray-50 p-6 rounded-xl">
                <p className="text-gray-700">{col}</p>
              </div>
            ))}
          </div>
        </section>
      );
    }

    case 'list':
      return (
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <ul className={`${props.style === 'numbered' ? 'list-decimal' : 'list-disc'} pl-6 space-y-2`}>
              {props.items?.map((item, i) => (
                <li key={i} className="text-gray-700">{item}</li>
              ))}
            </ul>
          </div>
        </section>
      );

    case 'quote':
      return (
        <section className="py-12 px-6">
          <div className={`max-w-3xl mx-auto ${props.style === 'bordered' ? 'border-l-4 border-green-500 pl-8' : ''}`}>
            <blockquote className="text-2xl italic text-gray-700 mb-4">
              "{props.text}"
            </blockquote>
            {props.author && (
              <cite className="text-gray-500 not-italic">— {props.author}</cite>
            )}
          </div>
        </section>
      );

    case 'spacer': {
      const heights = { small: 'h-12', medium: 'h-24', large: 'h-40' };
      return <div className={heights[props.height] || heights.medium} />;
    }

    case 'divider':
      return (
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <hr className={`border-t ${props.color === 'dark' ? 'border-gray-800' : 'border-gray-300'}`} />
          </div>
        </section>
      );

    case 'video':
      return (
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <div className={`relative ${props.aspectRatio === '16:9' ? 'pb-[56.25%]' : 'pb-[75%]'} rounded-xl overflow-hidden shadow-lg`}>
              <iframe
                src={props.url}
                className="absolute inset-0 w-full h-full"
                allowFullScreen
                title="Video"
              />
            </div>
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className={`py-16 px-6 ${props.style === 'gradient' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gray-100'}`}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={`text-4xl font-bold mb-4 ${props.style === 'gradient' ? 'text-white' : 'text-gray-900'}`}>
              {props.title}
            </h2>
            <p className={`text-xl mb-8 ${props.style === 'gradient' ? 'text-white/90' : 'text-gray-600'}`}>
              {props.description}
            </p>
            <a 
              href={props.buttonLink || '#'}
              className={`inline-block px-8 py-4 rounded-full font-bold text-lg transition-colors ${
                props.style === 'gradient' 
                  ? 'bg-white text-green-600 hover:bg-gray-100' 
                  : 'bg-green-500 text-white hover:bg-green-400'
              }`}
            >
              {props.buttonText}
            </a>
          </div>
        </section>
      );

    default:
      return null;
  }
};

export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/live-pages/public/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
        return;
      }
      const data = await response.json();
      setPage(data);
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-green-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
          <p className="text-xl text-gray-600 mb-8">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!page) return null;

  return (
    <div className="min-h-screen bg-white">
      {page.blocks.map((block, index) => (
        <BlockRenderer key={block.id || index} block={block} />
      ))}
      
      {page.blocks.length === 0 && (
        <div className="min-h-screen flex items-center justify-center text-gray-400">
          <p>This page has no content yet.</p>
        </div>
      )}
    </div>
  );
}
